/**
 * Générateur — Automatisme : mobiliser les connaissances sur la symétrie
 * axiale, le demi-tour, la translation.
 * Attendu ID : 1245 | Niveau : 3ème | Domaine : Grandeurs et mesures (base du
 * prof ; le programme range la ligne sous Espace et géométrie, Translations
 * et vecteurs, cycle 4 l. 1117 ; À ARBITRER)
 * Rubrique : AUTOMATISME (programme 2025).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTRACTION MULTI-GENS, cartes re-badgées (modèle 1094) :
 *   gen_1070 (automatisme 5e, symétrie axiale sur quadrillage) : le bon
 *     symétrique (QCM planche ①-④, V/F sur la figure) ;
 *   gen_951 (objectif 5e, demi-tour) : où est le centre ? (QCM croix),
 *     coordonnées du symétrique (saisie), image d'une figure sur quadrillage ;
 *   gen_182 (objectif 4e, translation) : construire l'image d'un triangle
 *     (placer les trois sommets, retour prof 13/09), coordonnées de l'image,
 *     ce que conserve une translation (V/F).
 * NEUF : « Quelle transformation ? » (QCM trois choix SANS aide dessinée : ni
 * axe ni centre ni flèche, « agrandissement » retiré : rotation, homothétie
 * et agrandissement sont absents du programme 2025) et un V/F fusionné sur
 * les trois transformations. ÉCARTÉS : 187 en entier (vecteur en
 * coordonnées = objectif 3e l. 1121), 182 transformation_qcm (la flèche est
 * la réponse), nature du quadrilatère et réciproque (objectifs 3e), angle,
 * frise, 1071 (gestes sur feuille blanche). Fiches 1070 / 951 au vieux
 * standard (À ARBITRER).
 *
 * INVARIANTS : les cartes déléguées gardent leurs invariants et leurs
 * solveurs (1070) ; la carte neuve construit l'image EXACTEMENT (translation
 * v, demi-tour 2O − M, symétrie d'axe vertical ou horizontal non tracé) sur
 * nœuds entiers dans la grille ; le solveur retrouve la transformation sur les
 * polygones rendus. Pas de sacrifice propre (fiches des gens sources).
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   glissement_pour_miroir : translation pour symétrie axiale, PRINCIPALE (QCM neuf) ;
 *   demi_tour_pour_axiale  : demi-tour pour symétrie axiale (et l'inverse) ;
 *   conservation_niee      : une transformation changerait les longueurs ;
 *   retournement_confondu  : la translation retournerait la figure (V/F fusionné) ;
 *   (1070) meme_cote, distance, horizontal ; (951) un_seul_sommet, centre_hors_milieu.
 *
 * ── Revue 2026-09-23 (vague 4, la translation en 4e ; dépendance seulement) ─
 * La revue de 182 et 187 a déplacé les deux cartes de construction dans 187
 * (« Il transforme une figure par translation », attendus de fin de 4e) : la
 * carte de 182 « Construire l'image par une translation » y est devenue
 * construire_placer_points_2d, « Coordonnées de l'image » y est devenue
 * image_point_texte_trou_saisie, et 187 ne parle plus de vecteur en
 * coordonnées (la raison qui l'avait fait écarter plus haut). DELEGUEES pointe
 * donc translation_construire_image et translation_coord_image_texte_trou vers
 * 187, translation_conservation_vf reste sur 182. Aucune clé de 1245 ne change,
 * les libellés 3e non plus, les coups de pouce (coupsDePouce/1245.js, par clé)
 * restent valables. Ce qui change pour l'élève : la construction dit que P, Q,
 * R sont les sommets d'un triangle et va dans toutes les directions ; l'image
 * d'un point se lit sur le repère gradué (A, B, P et la flèche tracés), les
 * coordonnées ne sont plus écrites dans le texte (À ARBITRER dans 187) ; le V/F
 * de conservation a quatre lignes, une à trois vraies, l'alignement en plus.
 * Solveurs suivis dans tests/solvers/transformations-3e.js (image d'un point
 * relue sur la figure, nouvelles lignes du V/F).
 */
import { randomChoice, randInt, shuffle, makeQcm, makeVraiFaux } from '../../../utils';
import { generate as gen1070 } from '../../5eme/transformations/gen_1070';
import { generate as gen951 } from '../../5eme/transformations/gen_951';
import { generate as gen182 } from '../../4eme/transformations/gen_182';
import { generate as gen187 } from '../../4eme/geometrie-plane/gen_187';
import { elementGrille, symCentral, BLEU, ORANGE } from '../../5eme/transformations/svgTransfo';

const ATTENDU_ID = 1245;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_TRANSFO = 'Quelle transformation ?';
const L_VF = 'Les trois transformations : vrai ou faux ?';

/** Cartes déléguées : clé propre → (gen source, clé source, libellé 3e). */
const DELEGUEES = {
    axiale_bon_qcm_unique: { gen: gen1070, cle: 'bon_qcm_unique', label: 'Symétrie axiale : le bon symétrique' },
    axiale_bon_tableau_vf: { gen: gen1070, cle: 'bon_tableau_vf', label: 'Symétrie axiale : le bon symétrique' },
    demitour_centre_qcm_unique: { gen: gen951, cle: 'centre_qcm_unique', label: 'Demi-tour : où est le centre ?' },
    demitour_coord_texte_trou_saisie: { gen: gen951, cle: 'coord_texte_trou_saisie', label: 'Demi-tour : coordonnées du symétrique' },
    demitour_image_grille_qcm_unique: { gen: gen951, cle: 'image_grille_qcm_unique', label: 'Demi-tour : image sur quadrillage' },
    // Revue 2026-09-23 : la construction et l'image d'un point vivent dans 187 (déplacées de 182).
    translation_construire_image: { gen: gen187, cle: 'construire_placer_points_2d', label: 'Translation : construire l\'image' },
    translation_coord_image_texte_trou: { gen: gen187, cle: 'image_point_texte_trou_saisie', label: 'Translation : coordonnées de l\'image' },
    translation_conservation_vf: { gen: gen182, cle: 'conservation_vf', label: 'Translation : ce qu\'elle conserve' },
};

const deleguer = (cle) => () => {
    const d = DELEGUEES[cle];
    const payload = d.gen(d.cle);
    payload.niveau = '3eme';
    payload.attendu = ATTENDU_ID;
    payload.label = d.label;
    payload._meta.params = { ...(payload._meta.params || {}), source: d.cle };
    return payload;
};

// ─── Carte neuve : quelle transformation ? (QCM 3 choix, sans aide dessinée) ──

const NOMS = { axiale: 'Une symétrie axiale.', centrale: 'Un demi-tour (symétrie centrale).', translation: 'Une translation.' };
const CODES = { axiale: { centrale: 'demi_tour_pour_axiale', translation: 'glissement_pour_miroir' }, centrale: { axiale: 'demi_tour_pour_axiale', translation: 'glissement_pour_miroir' }, translation: { axiale: 'glissement_pour_miroir', centrale: 'demi_tour_pour_axiale' } };
const EXPLICATIONS = {
    regle: 'Translation : la figure glisse sans tourner ni se retourner. Demi-tour : elle tourne à l\'envers autour d\'un point. Symétrie axiale : elle se retourne comme dans un miroir.',
    glissement_pour_miroir: 'Dans une translation, la figure garde le même sens ; ici elle est retournée comme dans un miroir : c\'est une symétrie axiale.',
    demi_tour_pour_axiale: 'Après un demi-tour, la figure est à l\'envers (tête en bas) ; après une symétrie axiale, elle est renversée comme dans un miroir, pas à l\'envers.',
    conservation_niee: 'Les trois transformations conservent les longueurs, les angles et les aires.',
    retournement_confondu: 'Seule la translation ne retourne pas la figure.',
};

/** Un triangle scalène (aucun côté égal, non aligné) dans une fenêtre : la transformation se voit. */
const triangleScalene = (xmin, xmax, ymin, ymax) => {
    for (let essai = 0; essai < 200; essai++) {
        const pts = [];
        while (pts.length < 3) { const p = [randInt(xmin, xmax), randInt(ymin, ymax)]; if (!pts.some((q) => q[0] === p[0] && q[1] === p[1])) pts.push(p); }
        const [a, b, c] = pts;
        const aire = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
        const d2 = (p, q) => (p[0] - q[0]) ** 2 + (p[1] - q[1]) ** 2;
        const cotes = [d2(a, b), d2(b, c), d2(c, a)];
        if (Math.abs(aire) < 3 || new Set(cotes).size < 3) continue;
        return pts;
    }
    return [[-3, -2], [0, -3], [-1, 1]];
};
const dansGrille = (p, b) => p[0] >= -b && p[0] <= b && p[1] >= -b && p[1] <= b;

const genTransformation = () => {
    const b = 5;
    const regime = randomChoice(['axiale', 'centrale', 'translation']);
    let src; let img; let params;
    for (let essai = 0; essai < 100; essai++) {
        src = regime === 'axiale' ? triangleScalene(-4, 2, -4, 2) : triangleScalene(-3, 3, -3, 3);
        if (regime === 'axiale') {
            const vertical = randInt(0, 1) === 1; const a = randomChoice([1, 2, 3, -1, -2, -3]);
            img = src.map(([x, y]) => (vertical ? [2 * a - x, y] : [x, 2 * a - y]));
            params = { regime, axe: vertical ? `x = ${a}` : `y = ${a}` };
        } else if (regime === 'centrale') {
            const O = [randInt(-1, 1), randInt(-1, 1)];
            img = src.map((p) => symCentral(p, O));
            params = { regime, centre: O };
        } else {
            const v = [randomChoice([-4, -3, 3, 4]), randomChoice([-3, -2, 2, 3])];
            img = src.map(([x, y]) => [x + v[0], y + v[1]]);
            params = { regime, vecteur: v };
        }
        // l'image tient dans la grille et ne chevauche pas la source (boîtes disjointes)
        const boite = (pts) => ({ x0: Math.min(...pts.map((q) => q[0])), x1: Math.max(...pts.map((q) => q[0])), y0: Math.min(...pts.map((q) => q[1])), y1: Math.max(...pts.map((q) => q[1])) });
        const B1 = boite(src); const B2 = boite(img);
        const disjointes = B1.x1 < B2.x0 || B2.x1 < B1.x0 || B1.y1 < B2.y0 || B2.y1 < B1.y0;
        if (img.every((p) => dansGrille(p, b)) && disjointes) break;
    }
    const bon = NOMS[regime];
    const faux = shuffle(Object.keys(NOMS).filter((r) => r !== regime)).map((r) => ({ v: NOMS[r], code: CODES[regime][r] }));
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, '<p><b>La figure orange est l\'image de la figure bleue.</b> Quelle transformation permet de passer de l\'une à l\'autre ?</p>', {
        ...opts(L_TRANSFO),
        elements_additionnels: elementGrille({ xMin: -b, yMin: -b, xMax: b, yMax: b, polys: [{ pts: src, couleur: BLEU }, { pts: img, couleur: ORANGE, dash: true }] }),
    });
    payload.contenu = { ...payload.contenu, sousType: regime, explications: EXPLICATIONS };
    payload._meta.params = { ...params, src, img, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte neuve : V/F fusionné sur les trois transformations ─────────────

const POOL_VF = [
    { l: 'Une translation ne retourne pas la figure : elle la fait glisser.', v: true, fam: 'translation' },
    { l: 'Une translation retourne la figure comme un miroir.', v: false, fam: 'translation', code: 'retournement_confondu' },
    { l: 'Après un demi-tour, la figure est à l\'envers (tête en bas).', v: true, fam: 'demi_tour' },
    { l: 'Un demi-tour fait glisser la figure sans la tourner.', v: false, fam: 'demi_tour', code: 'retournement_confondu' },
    { l: 'Une symétrie axiale retourne la figure comme dans un miroir.', v: true, fam: 'axiale' },
    { l: 'Une symétrie axiale fait tourner la figure d\'un demi-tour.', v: false, fam: 'axiale', code: 'demi_tour_pour_axiale' },
    { l: 'La symétrie axiale, le demi-tour et la translation conservent les longueurs.', v: true, fam: 'conservation' },
    { l: 'La symétrie axiale, le demi-tour et la translation conservent les aires.', v: true, fam: 'conservation' },
    { l: 'Un demi-tour change les longueurs de la figure.', v: false, fam: 'conservation', code: 'conservation_niee' },
    { l: 'Une symétrie axiale change les mesures des angles.', v: false, fam: 'conservation', code: 'conservation_niee' },
    { l: 'Un point situé sur l\'axe de symétrie est son propre symétrique.', v: true, fam: 'point_fixe' },
    { l: 'Le centre d\'un demi-tour est son propre symétrique.', v: true, fam: 'point_fixe' },
    { l: 'Dans une translation, un point peut être sa propre image.', v: false, fam: 'point_fixe', code: 'retournement_confondu' },
];
export const VERITES_VF = Object.fromEntries(POOL_VF.map((x) => [x.l, x.v]));

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(POOL_VF.filter((x) => !x.v)); const vraies = shuffle(POOL_VF.filter((x) => x.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((x) => x.fam)).size !== 4) continue;
        const payload = makeVraiFaux(lignes.map((x) => x.l), lignes.map((x) => (x.v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1245 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'cartes déléguées : invariants et distracteurs de 1070, 951, 182 et 187 (re-badgées 3e) ; rotation, homothétie, agrandissement jamais servis',
        'carte neuve : image exacte sur nœuds entiers, sans axe, centre ni flèche dessinés ; triangle scalène pour que le retournement se voie',
    ],
    transformation_qcm_unique: 'les deux autres transformations (glissement_pour_miroir, demi_tour_pour_axiale) : trois choix',
    sens_tableau_vf: 'quatre familles exclusives parmi translation, demi-tour, axiale, conservation, point fixe',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    ...Object.fromEntries(Object.keys(DELEGUEES).map((k) => [k, deleguer(k)])),
    transformation_qcm_unique: genTransformation,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'axiale', label: 'Symétrie axiale : le bon symétrique', formatsList: [{ key: 'axiale_bon_qcm_unique', label: 'QCM' }, { key: 'axiale_bon_tableau_vf', label: 'Vrai/Faux' }] },
    { id: 'demitour', label: 'Demi-tour', formatsList: [{ key: 'demitour_centre_qcm_unique', label: 'Où est le centre ? (QCM)' }, { key: 'demitour_coord_texte_trou_saisie', label: 'Coordonnées du symétrique' }, { key: 'demitour_image_grille_qcm_unique', label: 'Image sur quadrillage (QCM)' }] },
    { id: 'translation', label: 'Translation', formatsList: [{ key: 'translation_construire_image', label: 'Construire l\'image (placer)' }, { key: 'translation_coord_image_texte_trou', label: 'Coordonnées de l\'image' }, { key: 'translation_conservation_vf', label: 'Ce qu\'elle conserve (Vrai/Faux)' }] },
    { id: 'transformation', label: L_TRANSFO, formatsList: [{ key: 'transformation_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
